import React from "react";
import { Box, Grid, Typography, Chip } from "@mui/material";
import SectionCard from "./SectionCard";
import SectionHeader from "./SectionHeader";
import FieldRow from "./FieldRow";
import {
  VIVEN_LABELS,
  GRUPO_LABELS,
  EDUCACION_LABELS,
  SEXO_LABELS,
  FUMADOR_LABELS,
  ALCOHOL_LABELS,
} from "../hooks/useEntrevistaData";

const getOptionLabel = (options, value, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;
  return options[value] ?? String(value);
};

const formatValue = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
};

const buildList = (...items) => items.filter(Boolean);

const renderSummarySection = (title, items, emptyText = "Sin incidencias registradas") => (
  <Box className="entrevista-summary-block">
    <Typography className="entrevista-summary-block__title">{title}</Typography>
    {items.length > 0 ? (
      <Box className="entrevista-summary-block__chips">
        {items.map((item) => (
          <Chip
            key={`${title}-${item}`}
            label={item}
            size="small"
            className="entrevista-summary-chip"
            variant="outlined"
          />
        ))}
      </Box>
    ) : (
      <Typography className="entrevista-summary-block__empty">{emptyText}</Typography>
    )}
  </Box>
);

export default function EntrevistaView({ state, sectionRefs, sectionCardSx }) {
  const {
    codigo, createdAt, tipoEntrevista, nombre, apellidos, telefono, dateBirth,
    numViven, numGrupo, numNivelEducacional, numSexo, edad,
    venredi, terapiaOcupacional,
    numFumador, tiempoFumador, aniosDeshabilitacion,
    numAlcohol, tiempoAlcohol, aniosDeshabilitacionAlcohol,
    caidas12Meses, numCaidas, ayudaLocomocion,
    protesisArticulares, protesisRodilla, protesisCadera, protesisHombro,
    protesisManoDedos, protesisOtros,
    vertigos, mareosAndar,
    cardiovascular, hipertension, insuficienciaCardiaca, accCerebrovascular,
    anginaPecho, infarto, trombosis, otrosCardiovascular,
    respiratorio, asma, epoc, otrosRespiratorio,
    genitoUrinario, infeccionUrinaria, calculosRenales, insuficienciaRenal,
    incontinenciaUrinaria, prostata, otrosGenitoUrinario,
    endocrino, diabetes, hipotiroidismo, hipercolesterolemia, osteoporosis, otrosEndocrino,
    digestivo, herniaHiato, reflujo, pancreatitis, enfermedadHepatica, diverticulitis, otrosDigestivo,
    musculoEsqueletico, pieTobillo, rodilla, cadera, lumbar, dorsal, cervical, cabeza, hombro,
    codo, manoMunieca,
    nervioso, depresion, ansiedad, esclerosisMultiple, parkinson, alzheimer, deterioroCognitivo,
    neuropatiaPeriferica, otrosNervioso,
    sentidos, hipoacusia, tinitus, perdidaVision,
    datosInteres, numAnalgesicos,
    medicacion, antihipertensivos, antidiabeticos, estatinas, anticoagulantes, tiroides, diuretico,
    analgesicos, antidepresivos, ansioliticos, antiosteoporoticos, inhalador, protectorEstomago,
    otrosMedicacion,
    tensionPAS, tensionPAD, tensionPMM,
    actividadesExtras, actividadesSeleccionadas,
    caidasFinales, numPastillas, numAnalgesicosFinal,
    cambioMedicacionFinal, consultaMedico, ingresosHospital, datosInteresFinal,
  } = state;

  return (
    <>
      {/* Bloque 1: Datos del paciente */}
      <SectionCard
        id="pdf-content"
        className="entrevista-viewer__section is-readonly"
        ref={(node) => { sectionRefs.current.datos = node; }}
        sx={sectionCardSx}
      >
        <SectionHeader
          eyebrow="Bloque 1"
          title="Datos del paciente"
          description="Identificación, contexto sociodemográfico y situación basal del paciente."
        />

        <Grid container spacing={2} className="entrevista-summary-grid">
          <Grid item xs={12} md={4}><FieldRow label="Codigo" value={codigo} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Fecha" value={createdAt} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Tipo" value={tipoEntrevista} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Nombre" value={nombre} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Apellidos" value={apellidos} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Telefono" value={telefono} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Fecha de nacimiento" value={dateBirth} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Viven" value={getOptionLabel(VIVEN_LABELS, numViven)} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Grupo" value={getOptionLabel(GRUPO_LABELS, numGrupo)} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Nivel educacional" value={getOptionLabel(EDUCACION_LABELS, numNivelEducacional)} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Sexo" value={getOptionLabel(SEXO_LABELS, numSexo)} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Edad" value={edad} /></Grid>
          <Grid item xs={12}>
            {renderSummarySection(
              "Programas",
              buildList(venredi && "VENREDI", terapiaOcupacional && "Terapia ocupacional"),
              "Sin programas marcados"
            )}
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ marginBottom: 2, textAlign: { xs: "left", sm: "center" }, mt: 3 }}>
          HÁBITOS NOCIVOS
        </Typography>
        <Grid container spacing={2} className="entrevista-summary-grid">
          <Grid item xs={12} md={4}><FieldRow label="Fumador" value={getOptionLabel(FUMADOR_LABELS, numFumador)} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Tiempo fumador (años)" value={formatValue(tiempoFumador)} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Años de deshabilitación" value={formatValue(aniosDeshabilitacion)} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Alcohol" value={getOptionLabel(ALCOHOL_LABELS, numAlcohol)} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Tiempo alcohol (años)" value={formatValue(tiempoAlcohol)} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Años de deshabilitación" value={formatValue(aniosDeshabilitacionAlcohol)} /></Grid>
        </Grid>

        <Typography variant="h6" sx={{ marginBottom: 2, textAlign: "center", mt: 3 }}>
          HISTORIAL
        </Typography>
        <Grid container spacing={2} className="entrevista-summary-grid">
          <Grid item xs={12} md={4}><FieldRow label="Caidas en los ultimos 12 meses" value={caidas12Meses ? "Si" : "No"} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Numero de caidas" value={formatValue(numCaidas, "0")} /></Grid>
          <Grid item xs={12} md={4}><FieldRow label="Ayuda locomocion" value={ayudaLocomocion ? "Si" : "No"} /></Grid>
        </Grid>
      </SectionCard>

      {/* Bloque 2: Historial y sistemas */}
      <SectionCard
        id="pdf-content-2"
        className="entrevista-viewer__section is-readonly"
        ref={(node) => { sectionRefs.current.historial = node; }}
        sx={sectionCardSx}
      >
        <SectionHeader
          eyebrow="Bloque 2"
          title="Historial y sistemas"
          description="Registro estructurado de antecedentes, síntomas y hallazgos por sistemas."
        />
        <Grid container spacing={2} className="entrevista-summary-grid">
          <Grid item xs={12} md={6}>
            {renderSummarySection("Prótesis articulares", buildList(
              protesisArticulares && "Protesis articulares",
              protesisRodilla && "Rodilla",
              protesisCadera && "Cadera",
              protesisHombro && "Hombro",
              protesisManoDedos && "Manos o dedos",
              protesisOtros && `Otros: ${protesisOtros}`
            ))}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSummarySection("Vértigos o mareos", buildList(
              vertigos && "Vertigos",
              mareosAndar && "Mareos al andar"
            ))}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSummarySection("Sistema cardiovascular", buildList(
              cardiovascular && "Sistema cardiovascular",
              hipertension && "Hipertension",
              insuficienciaCardiaca && "Insuficiencia cardiaca",
              accCerebrovascular && "Accidente cerebrovascular",
              anginaPecho && "Angina de pecho",
              infarto && "Infarto",
              trombosis && "Trombosis",
              otrosCardiovascular && `Otros: ${otrosCardiovascular}`
            ))}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSummarySection("Sistema respiratorio", buildList(
              respiratorio && "Sistema respiratorio",
              asma && "Asma",
              epoc && "EPOC",
              otrosRespiratorio && `Otros: ${otrosRespiratorio}`
            ))}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSummarySection("Sistema genitourinario", buildList(
              genitoUrinario && "Sistema genitourinario",
              infeccionUrinaria && "Infeccion urinaria",
              calculosRenales && "Calculos renales",
              insuficienciaRenal && "Insuficiencia renal",
              incontinenciaUrinaria && "Incontinencia urinaria",
              prostata && "Prostata",
              otrosGenitoUrinario && `Otros: ${otrosGenitoUrinario}`
            ))}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSummarySection("Sistema endocrino", buildList(
              endocrino && "Sistema endocrino",
              diabetes && "Diabetes",
              hipotiroidismo && "Hipotiroidismo",
              hipercolesterolemia && "Hipercolesterolemia",
              osteoporosis && "Osteoporosis",
              otrosEndocrino && `Otros: ${otrosEndocrino}`
            ))}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSummarySection("Sistema digestivo", buildList(
              digestivo && "Sistema digestivo",
              herniaHiato && "Hernia de hiato",
              reflujo && "Reflujo",
              pancreatitis && "Pancreatitis",
              enfermedadHepatica && "Enfermedad hepatica",
              diverticulitis && "Diverticulitis",
              otrosDigestivo && `Otros: ${otrosDigestivo}`
            ))}
          </Grid>
        </Grid>
      </SectionCard>

      {/* Bloque 3: Medicación y seguimiento */}
      <SectionCard
        id="pdf-content-3"
        className="entrevista-viewer__section is-readonly"
        ref={(node) => { sectionRefs.current.medicacion = node; }}
        sx={sectionCardSx}
      >
        <SectionHeader
          eyebrow="Bloque 3"
          title="Medicación y seguimiento"
          description="Estado musculoesquelético, sistema nervioso, medicación y actividades del programa."
        />

        <Grid container spacing={2} className="entrevista-summary-grid">
          <Grid item xs={12} md={6}>
            {renderSummarySection("Sistema musculoesquelético", buildList(
              musculoEsqueletico && "Sistema musculoesqueletico",
              pieTobillo && "Pie y tobillo",
              rodilla && "Rodilla",
              cadera && "Cadera",
              lumbar && "Lumbar",
              dorsal && "Dorsal",
              cervical && "Cervical",
              cabeza && "Cabeza",
              hombro && "Hombro",
              codo && "Codo",
              manoMunieca && "Mano y munieca"
            ))}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSummarySection("Sistema nervioso", buildList(
              nervioso && "Sistema nervioso",
              depresion && "Depresion",
              ansiedad && "Ansiedad",
              esclerosisMultiple && "Esclerosis multiple",
              parkinson && "Parkinson",
              alzheimer && "Alzheimer",
              deterioroCognitivo && "Deterioro cognitivo",
              neuropatiaPeriferica && "Neuropatia periferica",
              otrosNervioso && `Otros: ${otrosNervioso}`
            ))}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSummarySection("Sistema de los sentidos", buildList(
              sentidos && "Sistema de los sentidos",
              hipoacusia && "Hipoacusia",
              tinitus && "Tinitus",
              perdidaVision && "Perdida de vision"
            ))}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSummarySection("Actividades", actividadesSeleccionadas)}
          </Grid>
        </Grid>

        <Grid container spacing={2} className="entrevista-summary-grid" sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <FieldRow label="Datos de interes" value={datosInteres} multiline />
          </Grid>
          <Grid item xs={12} md={3}>
            <FieldRow label="Numero de analgesicos" value={formatValue(numAnalgesicos, "0")} />
          </Grid>
          <Grid item xs={12} md={3}>
            <FieldRow label="Medicacion" value={medicacion ? "Si" : "No"} />
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldRow label="Tension PAS" value={formatValue(tensionPAS, "0")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldRow label="Tension PAD" value={formatValue(tensionPAD, "0")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldRow label="Frecuencia cardiaca" value={formatValue(tensionPMM, "0")} />
          </Grid>
        </Grid>

        <Grid container spacing={2} className="entrevista-summary-grid" sx={{ mt: 1 }}>
          <Grid item xs={12}>
            {renderSummarySection("Tratamiento farmacológico", buildList(
              medicacion && "Medicacion",
              antihipertensivos && "Antihipertensivos",
              antidiabeticos && "Antidiabeticos",
              estatinas && "Estatinas",
              anticoagulantes && "Anticoagulantes",
              tiroides && "TTO tiroides",
              diuretico && "Diuretico",
              analgesicos && "Analgesicos",
              antidepresivos && "Antidepresivos",
              ansioliticos && "Ansioliticos",
              antiosteoporoticos && "Antiosteoporoticos",
              inhalador && "Inhalador",
              protectorEstomago && "Protector de estomago",
              otrosMedicacion && `Otros: ${otrosMedicacion}`
            ))}
          </Grid>
        </Grid>

        {actividadesExtras.length > 0 && (
          <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
            Actividades registradas adicionales: {actividadesExtras.join(", ")}
          </Typography>
        )}
      </SectionCard>

      {/* Bloque 4: Entrevista final */}
      <SectionCard
        className="entrevista-viewer__section is-readonly"
        ref={(node) => { sectionRefs.current.final = node; }}
        sx={sectionCardSx}
      >
        <SectionHeader
          title="Entrevista final"
          description="Variables de seguimiento al cierre del periodo de participación."
        />
        <Grid container spacing={2} className="entrevista-summary-grid">
          <Grid item xs={12} md={4}>
            <FieldRow label="Caidas meses de participación" value={formatValue(caidasFinales, "0")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldRow label="Numero de pastillas" value={formatValue(numPastillas, "0")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldRow label="Numero de analgesicos" value={formatValue(numAnalgesicosFinal, "0")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldRow label="Cambio de medicacion" value={cambioMedicacionFinal ? "Si" : "No"} />
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldRow label="Consulta medico empeoramiento" value={consultaMedico ? "Si" : "No"} />
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldRow label="Ingresos en hospital" value={ingresosHospital ? "Si" : "No"} />
          </Grid>
          <Grid item xs={12}>
            <FieldRow label="Detalles final" value={datosInteresFinal} multiline />
          </Grid>
        </Grid>
      </SectionCard>
    </>
  );
}
